import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import DashboardShell from "../components/shell/DashboardShell";
import api, { formatApiError } from "../lib/api";
import { toast } from "sonner";
import {
  Activity,
  Filter,
  ChevronLeft,
  ChevronDown,
  Loader2,
  Home,
  FileSignature,
  Receipt,
  Landmark,
  Wrench,
  CreditCard,
  Users,
  ShieldAlert,
  LogIn,
  Download,
  RefreshCw,
  Search,
  X,
} from "lucide-react";

const TYPE_META = {
  property:    { label: "Property",    icon: Home,           tone: "bg-emerald-100 text-emerald-800 border-emerald-200" },
  lease:       { label: "Lease",       icon: FileSignature,  tone: "bg-blue-100 text-blue-800 border-blue-200" },
  expense:     { label: "Expense",     icon: Receipt,        tone: "bg-amber-100 text-amber-800 border-amber-200" },
  loan:        { label: "Loan",        icon: Landmark,       tone: "bg-violet-100 text-violet-800 border-violet-200" },
  work_order:  { label: "Work order",  icon: Wrench,         tone: "bg-orange-100 text-orange-800 border-orange-200" },
  payment:     { label: "Payment",     icon: CreditCard,     tone: "bg-teal-100 text-teal-800 border-teal-200" },
  tenant:      { label: "Tenant",      icon: Users,          tone: "bg-pink-100 text-pink-800 border-pink-200" },
  admin:       { label: "Admin",       icon: ShieldAlert,    tone: "bg-rose-100 text-rose-800 border-rose-200" },
  login:       { label: "Login",       icon: LogIn,          tone: "bg-slate-200 text-slate-700 border-slate-300" },
};

const PAGE_SIZE = 100;

export default function AdminLandlordActivity() {
  const { lid } = useParams();
  const [user, setUser] = useState(null);
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [hasMore, setHasMore] = useState(false);
  const [availableTypes, setAvailableTypes] = useState([]);
  const [typeFilter, setTypeFilter] = useState("");
  const [skip, setSkip] = useState(0);
  const [loading, setLoading] = useState(true);
  // Search input is decoupled from `query` — `query` only updates 300ms
  // after the last keystroke (debounce) so we don't refetch on every
  // character. The search itself is server-side (iter49+): the server
  // applies `q` to label / action / extras across the full event union
  // before paginating, so search hits the user's entire history, not just
  // the loaded page.
  const [searchInput, setSearchInput] = useState("");
  const [query, setQuery] = useState("");
  useEffect(() => {
    const t = setTimeout(() => setQuery(searchInput.trim().toLowerCase()), 300);
    return () => clearTimeout(t);
  }, [searchInput]);

  const load = async (opts = {}) => {
    const { append = false, freshSkip = 0 } = opts;
    setLoading(true);
    try {
      const params = new URLSearchParams();
      params.set("limit", String(PAGE_SIZE));
      params.set("skip", String(freshSkip));
      if (typeFilter) params.set("type", typeFilter);
      if (query) params.set("q", query);
      const { data } = await api.get(`/admin/landlords/${lid}/activity?${params.toString()}`);
      setUser(data.user);
      setItems(append ? [...items, ...(data.items || [])] : (data.items || []));
      setTotal(data.total || 0);
      setHasMore(!!data.has_more);
      setSkip(freshSkip + (data.items?.length || 0));
      if (data.available_types) setAvailableTypes(data.available_types);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Couldn't load activity");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load({ append: false, freshSkip: 0 });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [typeFilter, lid, query]);

  const exportCsv = async () => {
    // Reuse the JSON endpoint and convert client-side — small enough at
    // this scope (capped at ~50k events per user).
    try {
      const params = new URLSearchParams();
      params.set("limit", "50000");
      if (typeFilter) params.set("type", typeFilter);
      if (query) params.set("q", query);
      const { data } = await api.get(`/admin/landlords/${lid}/activity?${params.toString()}`);
      const lines = [
        ["Timestamp (UTC)", "Type", "Action", "Label", "Ref ID", "Extras"].join(","),
        ...data.items.map((e) => {
          const cells = [
            e.at || "",
            e.type || "",
            e.action || "",
            (e.label || "").replace(/"/g, '""'),
            e.ref_id || "",
            JSON.stringify(e.extras || {}).replace(/"/g, '""'),
          ];
          return cells.map((c) => `"${c}"`).join(",");
        }),
      ];
      const blob = new Blob([lines.join("\n")], { type: "text/csv;charset=utf-8" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `activity-${user?.email || lid}-${new Date().toISOString().slice(0, 10)}.csv`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success("Activity CSV downloaded");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Export failed");
    }
  };

  return (
    <DashboardShell>
      <div className="p-8 max-w-5xl mx-auto">
        <Link to="/dashboard/admin/landlords" className="inline-flex items-center gap-1 text-xs font-mono uppercase tracking-widest text-slate-500 hover:text-slate-900 mb-3" data-testid="activity-back-link">
          <ChevronLeft className="w-3.5 h-3.5" /> Back to platform users
        </Link>

        <div className="flex items-start justify-between mb-6 flex-wrap gap-3">
          <div>
            <div className="overline text-slate-500">Per-user activity</div>
            <h1 className="text-3xl font-bold text-slate-900 flex items-center gap-3" style={{ fontFamily: "Outfit" }} data-testid="activity-heading">
              <Activity className="w-7 h-7 text-slate-700" />
              {user?.full_name || user?.email || "Activity"}
            </h1>
            {user && (
              <p className="text-sm text-slate-500 mt-1 font-mono">
                {user.email}{user.company ? ` · ${user.company}` : ""}
              </p>
            )}
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <button onClick={exportCsv} className="btn-outline-dark text-xs flex items-center gap-1" data-testid="activity-export-csv">
              <Download className="w-3.5 h-3.5" /> Export CSV
            </button>
            <button onClick={() => load({ append: false, freshSkip: 0 })} className="btn-outline-dark text-xs flex items-center gap-1" data-testid="activity-refresh">
              <RefreshCw className="w-3.5 h-3.5" /> Refresh
            </button>
          </div>
        </div>

        {/* Search */}
        <div className="relative mb-4" data-testid="activity-search-wrapper">
          <Search className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none" />
          <input
            type="text"
            className="input-clean w-full pl-9 pr-9"
            placeholder="Search this user's events — tenant name, vendor, lender, status…"
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            data-testid="activity-search-input"
          />
          {searchInput && (
            <button
              onClick={() => setSearchInput("")}
              className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-700 p-1"
              data-testid="activity-search-clear"
              title="Clear search"
            >
              <X className="w-4 h-4" />
            </button>
          )}
        </div>

        <div className="flex items-center gap-2 mb-5 flex-wrap" data-testid="activity-filters">
          <span className="overline text-slate-500 inline-flex items-center gap-1">
            <Filter className="w-3 h-3" /> Filter
          </span>
          <button
            onClick={() => setTypeFilter("")}
            className={`text-xs font-mono uppercase tracking-widest px-2 py-1 rounded-sm border transition-colors ${
              typeFilter === "" ? "bg-slate-900 text-white border-slate-900" : "bg-white border-slate-200 text-slate-700 hover:bg-slate-50"
            }`}
            data-testid="activity-filter-all"
          >
            All ({total})
          </button>
          {availableTypes.map((t) => {
            const meta = TYPE_META[t] || { label: t, tone: "bg-slate-100 text-slate-700 border-slate-200" };
            return (
              <button
                key={t}
                onClick={() => setTypeFilter(t)}
                className={`text-xs font-mono uppercase tracking-widest px-2 py-1 rounded-sm border transition-colors ${
                  typeFilter === t ? "bg-slate-900 text-white border-slate-900" : `${meta.tone} hover:opacity-80`
                }`}
                data-testid={`activity-filter-${t}`}
              >
                {meta.label}
              </button>
            );
          })}
        </div>

        <div className="bg-white border border-slate-200 rounded-sm">
          {loading && items.length === 0 ? (
            <div className="py-16 text-center text-sm text-slate-500 flex items-center justify-center gap-2">
              <Loader2 className="w-5 h-5 animate-spin" /> Loading activity timeline…
            </div>
          ) : items.length === 0 ? (
            <div className="py-16 text-center text-sm text-slate-500" data-testid="activity-empty">
              {query
                ? <>No events match <span className="font-mono">"{query}"</span>. <button onClick={() => setSearchInput("")} className="underline hover:text-slate-900">Clear search</button>.</>
                : "No matching activity events."}
            </div>
          ) : (
            <ul className="divide-y divide-slate-100" data-testid="activity-timeline">
              {items.map((row, idx) => {
                const meta = TYPE_META[row.type] || { label: row.type, icon: Activity, tone: "bg-slate-100 text-slate-700 border-slate-200" };
                const Icon = meta.icon;
                const ts = row.at ? new Date(row.at) : null;
                return (
                  <li
                    key={`${row.ref_id || row.type}-${row.at}-${idx}`}
                    className="px-5 py-3.5 hover:bg-slate-50"
                    data-testid={`activity-row-${idx}`}
                  >
                    <div className="flex items-start gap-3">
                      <div className={`shrink-0 w-9 h-9 rounded-sm border flex items-center justify-center ${meta.tone}`}>
                        <Icon className="w-4 h-4" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap text-sm">
                          <span className="text-[10px] font-mono uppercase tracking-widest text-slate-500">{meta.label}</span>
                          <span className="font-semibold text-slate-900 truncate">{row.label}</span>
                          {row.action && row.action !== "created" && (
                            <span className="text-xs font-mono text-slate-500">· {row.action}</span>
                          )}
                        </div>
                        <div className="text-xs text-slate-500 font-mono mt-0.5">
                          {ts ? ts.toLocaleString() : "—"}
                          {row.extras?.by && <> · by {row.extras.by}</>}
                          {row.extras?.status && <> · status: {row.extras.status}</>}
                        </div>
                      </div>
                    </div>
                  </li>
                );
              })}
            </ul>
          )}
        </div>

        {hasMore && (
          <div className="flex justify-center mt-4">
            <button onClick={() => load({ append: true, freshSkip: skip })} disabled={loading} className="btn-outline-dark text-xs flex items-center gap-1 disabled:opacity-60" data-testid="activity-load-more">
              {loading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <ChevronDown className="w-3.5 h-3.5" />}
              Load older events
            </button>
          </div>
        )}

        <div className="text-[11px] text-slate-400 mt-4 text-center">
          {query ? (
            <>Showing {items.length} match{items.length === 1 ? "" : "es"} of {total} for <span className="font-mono">"{query}"</span></>
          ) : (
            <>Showing {items.length} of {total} event{total === 1 ? "" : "s"}{typeFilter && <> · filtered by <span className="font-mono">{typeFilter}</span></>}</>
          )}
        </div>
      </div>
    </DashboardShell>
  );
}
